import { StrictMode, useEffect } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router";
import { SWRConfig } from "swr";
import { Layout } from "./components/Layout.tsx";
import { Loading, ToastProvider } from "./components/ui.tsx";
import { onUnauthorized, useApi } from "./lib/api.ts";
import { live } from "./lib/live.ts";
import type { AuthState } from "./lib/types.ts";
import { Activity } from "./pages/Activity.tsx";
import { Bugs } from "./pages/Bugs.tsx";
import { Login } from "./pages/Login.tsx";
import { Overview } from "./pages/Overview.tsx";
import { ProjectPage } from "./pages/Project.tsx";
import { Projects } from "./pages/Projects.tsx";
import { SessionPage } from "./pages/Session.tsx";
import { Sessions } from "./pages/Sessions.tsx";
import { Setup } from "./pages/Setup.tsx";
import { Usage } from "./pages/Usage.tsx";
import { AccountSettings } from "./pages/settings/Account.tsx";
import { ClaudeSettings } from "./pages/settings/Claude.tsx";
import { DiscordSettings } from "./pages/settings/Discord.tsx";
import { ServerSettings } from "./pages/settings/Server.tsx";
import "./styles.css";

function App() {
  const auth = useApi<AuthState>("/api/auth/state", { revalidateOnFocus: true });
  const location = useLocation();

  useEffect(() => onUnauthorized(() => void auth.mutate()), [auth]);
  const ready = auth.data?.authenticated && auth.data.passwordSet;
  useEffect(() => {
    if (ready) live.start();
    else live.stop();
  }, [ready]);

  if (!auth.data) return auth.error ? <div className="center">Cannot reach the Switchboard server. Is it running?</div> : <Loading />;
  const a = auth.data;
  if (!a.passwordSet) return <Setup auth={a} onAuthChange={() => auth.mutate()} />;
  if (!a.authenticated) return <Login onLogin={() => auth.mutate()} />;
  if (!a.setupCompleted || location.pathname === "/setup") {
    if (a.setupCompleted && location.pathname === "/setup") return <Navigate to="/" replace />;
    return <Setup auth={a} onAuthChange={() => auth.mutate()} />;
  }

  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Overview />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/projects/:slug" element={<ProjectPage />} />
        <Route path="/sessions" element={<Sessions />} />
        <Route path="/sessions/:id" element={<SessionPage />} />
        <Route path="/bugs" element={<Bugs />} />
        <Route path="/activity" element={<Activity />} />
        <Route path="/usage" element={<Usage />} />
        <Route path="/settings" element={<Navigate to="/settings/discord" replace />} />
        <Route path="/settings/discord" element={<DiscordSettings />} />
        <Route path="/settings/claude" element={<ClaudeSettings />} />
        <Route path="/settings/server" element={<ServerSettings />} />
        <Route path="/settings/account" element={<AccountSettings onLogout={() => auth.mutate()} />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<div className="empty"><h3>Page not found</h3></div>} />
      </Routes>
    </Layout>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <SWRConfig value={{ shouldRetryOnError: (err) => (err as { status?: number })?.status !== 401 && (err as { status?: number })?.status !== 404 }}>
      <ToastProvider>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </ToastProvider>
    </SWRConfig>
  </StrictMode>,
);
